import React, { useState } from "react";
import { Dialog, DialogContent, DialogTrigger, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { requestNda } from "@/lib/api";
import { toast } from "sonner";
import { ArrowUpRight, CheckCircle2, FileDown } from "lucide-react";

export default function RequestDmfDialog({ compound, trigger }) {
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    organization: "",
    title: "",
    intended_use: "",
  });

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const reset = () => {
    setDone(false);
    setForm({ name: "", email: "", organization: "", title: "", intended_use: "" });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.organization || !form.intended_use) {
      toast.error("Name, email, organization and intended use are required.");
      return;
    }
    try {
      setLoading(true);
      await requestNda({
        ...form,
        compound_id: compound.id,
        compound_name: compound.name,
        base_url: window.location.origin,
      });
      setDone(true);
      toast.success("Sent. Check your inbox for the NDA and dossier.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong.";
      toast.error(String(msg));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) setTimeout(reset, 300);
      }}
    >
      <DialogTrigger asChild>
        {trigger || (
          <button
            data-testid={`request-dmf-${compound.id}`}
            className="inline-flex items-center gap-2 font-mono-tight text-[10px] text-ink/80 hover:text-oxred link-underline"
          >
            <FileDown size={12} /> Request DMF
          </button>
        )}
      </DialogTrigger>
      <DialogContent
        className="!rounded-none !max-w-2xl !bg-bone !border !border-ink/15 !p-0 !gap-0 [&>button]:!hidden"
      >
        <DialogTitle className="sr-only">Request the {compound.name || compound.id} DMF dossier</DialogTitle>
        <DialogDescription className="sr-only">
          Provide your name, email, organization and intended use. We will email a mutual NDA and a link to the DMF-ready dossier excerpt within seconds.
        </DialogDescription>
        <div className="p-8 md:p-12 max-h-[85vh] overflow-y-auto">
          {done ? (
            <div data-testid="nda-success" className="flex flex-col items-start">
              <CheckCircle2 size={36} className="text-oxred" />
              <h2 className="font-display text-4xl md:text-5xl mt-6 tracking-tight leading-tight">
                Check your inbox.
              </h2>
              <p className="mt-4 text-ink/70 max-w-md">
                We&apos;ve sent your NDA and the DMF dossier excerpt for <strong>{compound.name || compound.id}</strong> to{" "}
                <span className="font-mono-tight text-[11px]">{form.email}</span>. Links are valid for 14 days.
              </p>
              <button
                data-testid="nda-close"
                onClick={() => setOpen(false)}
                className="mt-8 px-8 py-4 bg-ink text-bone font-mono-tight text-[11px] inline-flex items-center gap-3"
              >
                <span className="w-1.5 h-1.5 bg-oxred inline-block" /> Close
              </button>
            </div>
          ) : (
            <>
              <div className="flex items-baseline justify-between">
                <span className="font-mono-tight text-[10px] text-ink/60">DMF · REQUEST</span>
                <span className="font-mono-tight text-[10px] text-oxred">
                  {compound.id}
                </span>
              </div>
              <h2 className="font-display text-3xl md:text-5xl tracking-tight leading-[1.02] mt-6 max-w-xl">
                Request the <em className="italic text-oxred">{compound.name || compound.id}</em> dossier.
              </h2>
              <p className="mt-4 text-ink/70 text-sm max-w-md">
                Tell us who you are. We&apos;ll email you a mutual NDA and a link to the DMF-ready dossier excerpt.
                Links are personal and expire in 14 days.
              </p>

              <form onSubmit={submit} data-testid="nda-form" className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
                <div>
                  <label className="font-mono-tight text-[10px] text-ink/60">Full name*</label>
                  <input className="field" data-testid="nda-input-name" value={form.name} onChange={upd("name")} required />
                </div>
                <div>
                  <label className="font-mono-tight text-[10px] text-ink/60">Work email*</label>
                  <input className="field" data-testid="nda-input-email" type="email" value={form.email} onChange={upd("email")} required />
                </div>
                <div>
                  <label className="font-mono-tight text-[10px] text-ink/60">Organization*</label>
                  <input className="field" data-testid="nda-input-org" value={form.organization} onChange={upd("organization")} required />
                </div>
                <div>
                  <label className="font-mono-tight text-[10px] text-ink/60">Title</label>
                  <input className="field" data-testid="nda-input-title" value={form.title} onChange={upd("title")} />
                </div>
                <div className="md:col-span-2 mt-4">
                  <label className="font-mono-tight text-[10px] text-ink/60">Intended use*</label>
                  <textarea
                    className="field"
                    data-testid="nda-input-use"
                    rows={3}
                    placeholder="Licensing evaluation · research collaboration · procurement · other"
                    value={form.intended_use}
                    onChange={upd("intended_use")}
                    required
                  />
                </div>
                <div className="md:col-span-2 mt-10 flex items-center justify-between border-t border-ink/15 pt-6">
                  <span className="font-mono-tight text-[10px] text-ink/60">
                    LINKS EXPIRE IN 14 DAYS
                  </span>
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="nda-submit"
                    className="group inline-flex items-center gap-3 px-8 py-4 bg-ink text-bone font-mono-tight text-[11px] disabled:opacity-50"
                  >
                    <span className="w-1.5 h-1.5 bg-oxred inline-block" />
                    {loading ? "Sending…" : "Send me the NDA"}
                    <ArrowUpRight size={14} className="transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
